import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Drill, Briefcase } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';
import { PageHeader, LoadingState, DataTable, type Column } from '@/components/common';
import {
  DetailLayout,
  DetailList,
  MetricBars,
  RailCard,
  StatStrip,
} from '@/components/common/DetailPanels';
import {
  jobsApi,
  type Job,
  type JobConsumptionSummary,
  type JobReadiness,
} from '@/api/services/jobs.api';
import { formatCurrency, formatDate, formatNumber, toTitleCase } from '@/lib/utils';
import { JOB_STATUS_TONE } from '@/lib/job-status';
import { getErrorMessage } from '@/api/client';
import { JobReadinessPanel } from '@/components/jobs/JobReadinessPanel';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

type JobDetailData = Job & JobConsumptionSummary;
type ByItem = JobConsumptionSummary['consumption']['byItem'][number];

/**
 * One job — the well it is on, and what has been charged to it.
 *
 * A job number is what a whole crew refers to for weeks: it is on the AFE, on
 * every consumption record, and in the conversation about why a well went over
 * budget. Reading it in a sheet meant the one identifier everybody quotes was
 * the one thing that could not be sent.
 */
export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobDetailData | null>(null);
  const [readiness, setReadiness] = useState<JobReadiness | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    jobsApi
      .getById(id)
      .then((data) => !cancelled && setJob(data))
      .catch((error) => {
        if (cancelled) return;
        toast.error(getErrorMessage(error));
        navigate('/jobs');
      })
      .finally(() => !cancelled && setLoading(false));

    // Loaded separately: readiness is the first thing somebody wants and the
    // page should not wait on it, nor fail if a gate cannot be computed.
    jobsApi
      .getReadiness(id)
      .then((data) => !cancelled && setReadiness(data))
      .catch(() => {
        /* the panel simply does not render */
      });

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading) return <LoadingState variant="detail" />;
  if (!job) return null;

  const { consumption } = job;
  const variance = consumption.costVariance;

  const itemColumns: Column<ByItem>[] = [
    {
      key: 'sku',
      header: 'Item',
      sortable: true,
      linkTo: (i) => `/inventory/${i.itemId}`,
      render: (i) => (
        <div>
          <p className="font-medium">{i.sku}</p>
          <p className="text-xs text-muted-foreground">{i.name}</p>
        </div>
      ),
    },
    {
      key: 'quantity',
      header: 'Used',
      sortable: true,
      className: 'text-right',
      render: (i) => (
        <span className="block text-right tabular-nums">
          {formatNumber(i.quantity)} {i.unit}
        </span>
      ),
    },
    {
      key: 'plannedQuantity',
      header: 'Planned',
      sortable: true,
      className: 'text-right',
      render: (i) => (
        <span className="block text-right tabular-nums text-muted-foreground">
          {i.plannedQuantity ? `${formatNumber(i.plannedQuantity)} ${i.unit}` : '—'}
        </span>
      ),
    },
    {
      key: 'cost',
      header: 'Cost',
      sortable: true,
      className: 'text-right',
      render: (i) => (
        <span className="block text-right tabular-nums">{formatCurrency(i.cost)}</span>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Jobs', href: '/jobs' },
            { label: job.jobNumber },
          ]}
          title={job.name}
          description={[job.jobNumber, job.jobType ? toTitleCase(job.jobType) : null]
            .filter(Boolean)
            .join(' · ')}
          titleAdornment={
            <Badge className={JOB_STATUS_TONE[job.status] ?? ''}>{toTitleCase(job.status)}</Badge>
          }
        />
      </motion.div>

      <motion.div variants={fadeUp}>
        <DetailLayout
          rail={
            <>
              <RailCard title="Well" icon={<Drill className="h-4 w-4 text-muted-foreground" />}>
                <DetailList
                  items={[
                    // Read off the well record now, not off five text fields
                    // repeated on the job. Two jobs on one well agreed only by
                    // luck before, which is why the well is its own row.
                    { label: 'Well', value: job.well?.name, when: !!job.well },
                    {
                      label: 'Well number',
                      value: job.well?.wellNumber,
                      when: !!job.well?.wellNumber,
                    },
                    { label: 'API', value: job.well?.apiNumber, when: !!job.well },
                    {
                      label: 'Operator',
                      value: job.well?.operatorName,
                      when: !!job.well?.operatorName,
                    },
                    { label: 'Rig', value: job.rigName, when: !!job.rigName },
                    { label: 'Lease', value: job.well?.leaseName, when: !!job.well?.leaseName },
                    { label: 'Field', value: job.well?.fieldName, when: !!job.well?.fieldName },
                    {
                      label: 'Planned work',
                      value: job.plannedBaseMetric
                        ? `${formatNumber(job.plannedBaseMetric)} ${job.baseMetricUnit ?? ''}`.trim()
                        : null,
                      when: !!job.plannedBaseMetric,
                    },
                  ]}
                />
              </RailCard>

              <RailCard
                title="Commercial"
                icon={<Briefcase className="h-4 w-4 text-muted-foreground" />}
              >
                <DetailList
                  items={[
                    { label: 'Customer', value: job.customerName, when: !!job.customerName },
                    { label: 'AFE', value: job.afeNumber, when: !!job.afeNumber },
                    {
                      label: 'Location',
                      value: job.location ? (
                        <Link
                          to={`/locations/${job.location.id}`}
                          className="underline-offset-4 hover:underline"
                        >
                          {job.location.name}
                        </Link>
                      ) : null,
                      when: !!job.location,
                    },
                    {
                      label: 'Start',
                      value: job.startDate ? formatDate(job.startDate) : null,
                      when: !!job.startDate,
                    },
                    {
                      label: 'End',
                      value: job.endDate ? formatDate(job.endDate) : null,
                      when: !!job.endDate,
                    },
                    { label: 'Charges', value: consumption.recordCount },
                  ]}
                />
              </RailCard>
            </>
          }
          main={
            <div className="space-y-6">
              {/* First, because it is the question asked on the morning of a
                  mobilisation and the only one with a deadline. */}
              {readiness && <JobReadinessPanel readiness={readiness} />}

              {/* Field Pulse does not bill — that is settled in the operator's
                  ERP — but a charge sitting unapproved here never reaches it,
                  and the job is where somebody would notice. */}
              {consumption.unapproved.count > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    <strong>
                      {consumption.unapproved.count} charge
                      {consumption.unapproved.count === 1 ? '' : 's'}
                    </strong>{' '}
                    worth {formatCurrency(consumption.unapproved.cost)} are still awaiting approval,
                    so they have not gone anywhere yet.
                  </AlertDescription>
                </Alert>
              )}

              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Consumption</h3>
                  <CardDescription>Material charged to this job against plan</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <StatStrip
                    stats={[
                      { label: 'Consumed', value: formatCurrency(consumption.totalCost) },
                      {
                        // What the next bid is priced from. Not a percentage
                        // complete — nothing here records work actually done,
                        // and spend presented as progress makes an overspending
                        // job look ahead of schedule.
                        label: job.againstPlan.unit
                          ? `Per ${job.againstPlan.unit}`
                          : 'Per unit of work',
                        value:
                          job.againstPlan.costPerUnit != null
                            ? formatCurrency(job.againstPlan.costPerUnit)
                            : '—',
                      },
                      {
                        label: 'Planned',
                        value: consumption.plannedCost
                          ? formatCurrency(consumption.plannedCost)
                          : '—',
                      },
                      {
                        label: 'Variance',
                        value:
                          variance == null ? (
                            '—'
                          ) : (
                            // Over budget is the case somebody has to answer
                            // for, so the sign is kept and coloured.
                            <span
                              className={
                                variance > 0
                                  ? 'text-status-destructive-foreground'
                                  : 'text-status-success-foreground'
                              }
                            >
                              {variance > 0 ? '+' : ''}
                              {formatCurrency(variance)}
                            </span>
                          ),
                      },
                    ]}
                  />

                  {consumption.byPhase.length > 0 && (
                    <div className="space-y-3 border-t pt-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        By phase
                      </p>
                      <MetricBars
                        rows={consumption.byPhase.map((p) => ({
                          label: toTitleCase(p.phase),
                          value: p.cost,
                        }))}
                        unit=""
                        format={formatCurrency}
                      />
                    </div>
                  )}

                  {consumption.recordCount === 0 && (
                    <p className="border-t pt-4 text-sm text-muted-foreground">
                      No material has been charged to this job yet.
                    </p>
                  )}
                </CardContent>
              </Card>

              {consumption.byItem.length > 0 && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">By item</h3>
                    <CardDescription>
                      What was drawn against this job, and what it cost
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <DataTable<ByItem>
                      pagination
                      data={consumption.byItem}
                      columns={itemColumns}
                      emptyTitle="Nothing charged"
                      emptyDescription="No material has been drawn against this job."
                    />
                  </CardContent>
                </Card>
              )}

              {job.notes && (
                <Card>
                  <CardHeader>
                    <h3 className="text-base font-semibold text-foreground">Notes</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap text-sm text-muted-foreground">{job.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
